/**
 * Helpers de aleatoriedade.
 *
 * O RNG é injetável (parâmetro `rng`) para que QuestionEngine e outros
 * sistemas possam ser testados de forma determinística — basta passar uma
 * sequência conhecida via Vitest. Em produção, omitir cai em Math.random.
 */

export type Rng = () => number;

export const defaultRng: Rng = Math.random;

/** Inteiro em [min, max] inclusivo. */
export function randomInt(min: number, max: number, rng: Rng = defaultRng): number {
  const lo = Math.ceil(min);
  const hi = Math.floor(max);
  return Math.floor(rng() * (hi - lo + 1)) + lo;
}

export function pickOne<T>(items: readonly T[], rng: Rng = defaultRng): T {
  if (items.length === 0) {
    throw new Error('pickOne: lista vazia');
  }
  return items[Math.floor(rng() * items.length)]!;
}

/** Embaralha (Fisher–Yates) sem mutar a entrada. */
export function shuffle<T>(items: readonly T[], rng: Rng = defaultRng): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [copy[i], copy[j]] = [copy[j]!, copy[i]!];
  }
  return copy;
}

/**
 * Sorteio ponderado: cada item tem um peso ≥ 0. Itens com peso maior são
 * proporcionalmente mais prováveis. Usado pela seleção adaptativa de
 * tabuadas (errar mais → aparecer mais).
 */
export function weightedPick<T>(items: readonly T[], weights: readonly number[], rng: Rng = defaultRng): T {
  if (items.length === 0 || items.length !== weights.length) {
    throw new Error('weightedPick: itens e pesos devem ter o mesmo tamanho > 0');
  }
  const total = weights.reduce((sum, w) => sum + Math.max(0, w), 0);
  if (total <= 0) {
    return pickOne(items, rng);
  }
  let r = rng() * total;
  for (let i = 0; i < items.length; i++) {
    r -= Math.max(0, weights[i]!);
    if (r <= 0) return items[i]!;
  }
  return items[items.length - 1]!;
}
